import React, { useMemo } from 'react';
import './CustomLiquidGlass.scss';

export interface CustomLiquidGlassProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  noTint?: boolean;
  isFab?: boolean;
}

/**
 * Liquid glass component based on macOS effect
 * Uses SVG filters with backdrop blur for realistic glass distortion
 */
export const CustomLiquidGlass: React.FC<CustomLiquidGlassProps> = ({
  children,
  className = '',
  style = {},
}) => {
  const filterId = useMemo(() => `glass-distortion-${crypto.randomUUID()}`, []);

  return (
    <>
      <svg style={{ display: 'none' }}>
        <filter
          id={filterId}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
          filterUnits="objectBoundingBox"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.03 0.05"
            numOctaves="3"
            seed="5"
            result="turbulence"
          />

          <feGaussianBlur in="turbulence" stdDeviation="2" result="softMap" />

          <feColorMatrix
            in="softMap"
            type="matrix"
            values="1 0 0 0 0
                    0 1 0 0 0
                    0 0 1 0 0
                    0 0 0 10 -15"
            result="contrast"
          />

          <feDisplacementMap
            in="SourceGraphic"
            in2="contrast"
            scale="-35"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>

      <div
        className={`liquid-glass-wrapper ${className}`}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '12px',
          ...style,
        }}
      >
        {/* Layer 1: Distortion + Blur */}
        <div
          className="liquid-glass-effect"
          style={{
            position: 'absolute',
            zIndex: 0,
            inset: 0,
            backdropFilter: 'blur(2px) saturate(180%) contrast(1.2) brightness(1.1)',
            WebkitBackdropFilter: 'blur(2px) saturate(180%) contrast(1.2) brightness(1.1)',
            filter: `url(#${filterId})`,
            WebkitFilter: `url(#${filterId})`,
          }}
        />

        {/* Layer 2: Semi-transparent background for text contrast */}
        <div
          className="liquid-glass-tint"
          style={{
            position: 'absolute',
            zIndex: 1,
            inset: 0,
            background: 'transparent',
          }}
        />

        {/* Layer 3: Glossy Shine */}
        <div
          className="liquid-glass-shine"
          style={{
            position: 'absolute',
            zIndex: 2,
            inset: 0,
            overflow: 'hidden',
          }}
        />

        {/* Layer 4: Content */}
        <div
          className="liquid-glass-content"
          style={{
            position: 'relative',
            zIndex: 3,
            width: '100%',
            height: '100%',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
};
